import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import moment from 'moment';
import { toast } from 'react-toastify';
import { ShieldCheckIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { TASK_TYPE_LABELS } from '@claimup/shared/constants/assignments';
import MainLayout from '../../layouts/MainLayout';
import userStore from '../../store/userStore';
import api, { getErrorMessage } from '../../services/api';

/**
 * La auditoría del cliente (docs/ar-auditoria-plan.md): desde aquí la empresa
 * revisa que todo funciona. Dos pestañas:
 *
 *   · Actividad: todo lo que han hecho el agente de IA y las personas en sus
 *     expedientes AR, con filtros.
 *   · Pendiente: tareas vencidas (de quién y desde cuándo) y expedientes en
 *     gestión sin ninguna tarea.
 *
 * El gestor ve lo mismo, pero solo lo suyo y lo del agente: lo decide el servidor.
 */
export const KIND_LABELS = {
  ai_action: 'Acción de la IA',
  review: 'Revisión de la IA',
  status_change: 'Cambio de estado',
  call: 'Llamada',
  message: 'Mensaje',
  task_done: 'Tarea cerrada',
  annotation: 'Anotación',
};

const hoy = () => moment().format('YYYY-MM-DD');

const Enlace = ({ id, children }) => (
  <a href={`/view/${id}`} target='_blank' rel='noreferrer' className='text-blue-600 hover:underline'>
    {children || 'Expediente'}
  </a>
);

export const Actividad = () => {
  const [filtros, setFiltros] = useState({ from: hoy(), to: hoy(), actor: '', kind: '', task_type: '' });
  const [pagina, setPagina] = useState(1);
  const [datos, setDatos] = useState({ items: [], total: 0, pageSize: 50, people: [] });

  const cargar = useCallback(async () => {
    const params = Object.fromEntries(Object.entries({ ...filtros, page: pagina }).filter(([, v]) => v));
    try {
      const { data } = await api.get('/audit/activity', { params });
      setDatos(data.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }, [filtros, pagina]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const cambiar = (campo, valor) => {
    setPagina(1);
    setFiltros(f => ({ ...f, [campo]: valor }));
  };
  const paginas = Math.max(1, Math.ceil((datos.total || 0) / (datos.pageSize || 50)));

  return (
    <div className='bg-white border border-slate-200 rounded-lg p-4 space-y-4'>
      <div className='flex flex-wrap items-end gap-3 text-sm'>
        <label className='flex flex-col text-slate-600'>
          Desde
          <input type='date' value={filtros.from} onChange={e => cambiar('from', e.target.value)} className='mt-1 rounded-lg border border-slate-300 px-2 py-1' />
        </label>
        <label className='flex flex-col text-slate-600'>
          Hasta
          <input type='date' value={filtros.to} onChange={e => cambiar('to', e.target.value)} className='mt-1 rounded-lg border border-slate-300 px-2 py-1' />
        </label>
        <label className='flex flex-col text-slate-600'>
          Quién
          <select value={filtros.actor} onChange={e => cambiar('actor', e.target.value)} className='mt-1 rounded-lg border border-slate-300 px-2 py-1'>
            <option value=''>Todos</option>
            <option value='ai'>Agente IA</option>
            <option value='people'>Personas</option>
            {datos.people.map(p => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        <label className='flex flex-col text-slate-600'>
          Qué
          <select value={filtros.kind} onChange={e => cambiar('kind', e.target.value)} className='mt-1 rounded-lg border border-slate-300 px-2 py-1'>
            <option value=''>Todo</option>
            {Object.entries(KIND_LABELS).map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <label className='flex flex-col text-slate-600'>
          Tipo de tarea
          <select
            value={filtros.task_type}
            onChange={e => cambiar('task_type', e.target.value)}
            className='mt-1 rounded-lg border border-slate-300 px-2 py-1'
          >
            <option value=''>Todas</option>
            {Object.entries(TASK_TYPE_LABELS).map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <span className='ml-auto text-slate-500'>{datos.total} registros</span>
      </div>

      {datos.items.length === 0 ? (
        <p className='text-sm text-slate-500'>No hay actividad con esos filtros.</p>
      ) : (
        <div className='overflow-x-auto'>
          <table className='min-w-full text-sm'>
            <thead>
              <tr className='text-left text-xs uppercase text-slate-500'>
                <th className='py-2 pr-3'>Cuándo</th>
                <th className='py-2 pr-3'>Quién</th>
                <th className='py-2 pr-3'>Qué</th>
                <th className='py-2 pr-3'>Deudor</th>
                <th className='py-2 pr-3'>Detalle</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-slate-100'>
              {datos.items.map(f => (
                <tr key={f.key}>
                  <td className='py-2 pr-3 whitespace-nowrap text-slate-600'>{moment(f.at).format('DD/MM HH:mm')}</td>
                  <td className='py-2 pr-3 whitespace-nowrap'>{f.actor_name}</td>
                  <td className='py-2 pr-3 whitespace-nowrap'>{f.summary}</td>
                  <td className='py-2 pr-3'>
                    <Enlace id={f.expedient_id}>{f.deudor}</Enlace>
                  </td>
                  <td className='py-2 pr-3 max-w-md truncate text-slate-700' title={f.detail}>
                    {f.detail}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {paginas > 1 && (
        <div className='flex items-center gap-2 text-sm'>
          <button type='button' disabled={pagina <= 1} onClick={() => setPagina(p => p - 1)} className='rounded-lg border border-slate-300 px-3 py-1 disabled:opacity-50'>
            Anterior
          </button>
          <span className='text-slate-500'>
            {pagina} / {paginas}
          </span>
          <button type='button' disabled={pagina >= paginas} onClick={() => setPagina(p => p + 1)} className='rounded-lg border border-slate-300 px-3 py-1 disabled:opacity-50'>
            Siguiente
          </button>
        </div>
      )}
    </div>
  );
};

/** El buscador de las listas: como el de Reclamaciones. */
const Buscador = ({ value, onChange, placeholder }) => (
  <div className='relative max-w-sm'>
    <MagnifyingGlassIcon className='h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2' />
    <input
      type='search'
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      aria-label={placeholder}
      className='w-full rounded-lg border border-slate-300 pl-8 pr-2 py-1.5 text-sm'
    />
  </div>
);

const contiene = (texto, q) =>
  String(texto || '')
    .toLowerCase()
    .includes(q.trim().toLowerCase());

export const Pendiente = () => {
  const [datos, setDatos] = useState(null);
  const [persona, setPersona] = useState('');
  const [tipo, setTipo] = useState('');
  const [buscarVencidas, setBuscarVencidas] = useState('');
  const [buscarSinTarea, setBuscarSinTarea] = useState('');

  useEffect(() => {
    api
      .get('/audit/pending')
      .then(({ data }) => setDatos(data.data))
      .catch(err => toast.error(getErrorMessage(err)));
  }, []);

  if (!datos) return null;

  // Las dos listas llegan enteras: se filtran aquí.
  const personas = [...new Set(datos.overdue.map(o => o.assigned_name))].sort();
  const tipos = [...new Set(datos.overdue.map(o => o.task_type).filter(Boolean))].sort();
  const vencidas = datos.overdue.filter(
    o =>
      (!persona || o.assigned_name === persona) &&
      (!tipo || o.task_type === tipo) &&
      (!buscarVencidas || contiene(o.deudor, buscarVencidas) || contiene(o.description, buscarVencidas))
  );
  const sinTarea = datos.without_tasks.filter(
    w => !buscarSinTarea || contiene(w.deudor, buscarSinTarea) || contiene(w.estado, buscarSinTarea)
  );

  return (
    <div className='space-y-4'>
      <div className='bg-white border border-slate-200 rounded-lg p-4'>
        <h2 className='text-sm font-semibold text-slate-800 mb-2'>
          Tareas vencidas ({vencidas.length === datos.overdue.length ? datos.overdue.length : `${vencidas.length} de ${datos.overdue.length}`})
        </h2>
        {datos.overdue.length > 0 && (
          <div className='flex flex-wrap items-end gap-3 mb-3 text-sm'>
            <Buscador value={buscarVencidas} onChange={setBuscarVencidas} placeholder='Buscar deudor o tarea' />
            <label className='flex flex-col text-slate-600'>
              De quién
              <select value={persona} onChange={e => setPersona(e.target.value)} className='mt-1 rounded-lg border border-slate-300 px-2 py-1'>
                <option value=''>Todos</option>
                {personas.map(p => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </label>
            <label className='flex flex-col text-slate-600'>
              Tipo de tarea
              <select value={tipo} onChange={e => setTipo(e.target.value)} className='mt-1 rounded-lg border border-slate-300 px-2 py-1'>
                <option value=''>Todas</option>
                {tipos.map(t => (
                  <option key={t} value={t}>
                    {TASK_TYPE_LABELS[t] || t}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}
        {datos.overdue.length === 0 ? (
          <p className='text-sm text-slate-500'>No hay tareas vencidas.</p>
        ) : vencidas.length === 0 ? (
          <p className='text-sm text-slate-500'>Ninguna tarea vencida con esos filtros.</p>
        ) : (
          <table className='min-w-full text-sm'>
            <thead>
              <tr className='text-left text-xs uppercase text-slate-500'>
                <th className='py-2 pr-3'>Deudor</th>
                <th className='py-2 pr-3'>Tarea</th>
                <th className='py-2 pr-3'>De quién</th>
                <th className='py-2 pr-3'>Vencía</th>
                <th className='py-2 pr-3'>Retraso</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-slate-100'>
              {vencidas.map(o => (
                <tr key={o.assignment_id}>
                  <td className='py-2 pr-3'>
                    <Enlace id={o.expedient_id}>{o.deudor}</Enlace>
                  </td>
                  <td className='py-2 pr-3 text-slate-700'>
                    {o.description || TASK_TYPE_LABELS[o.task_type] || o.task_type}
                    {o.description && o.task_type && (
                      <span className='block text-xs text-slate-400'>{TASK_TYPE_LABELS[o.task_type] || o.task_type}</span>
                    )}
                  </td>
                  <td className='py-2 pr-3'>{o.assigned_name}</td>
                  <td className='py-2 pr-3 whitespace-nowrap text-slate-600'>{moment(o.due).format('DD/MM/YYYY')}</td>
                  <td className='py-2 pr-3 whitespace-nowrap font-medium text-red-700'>{o.days_overdue} días</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className='bg-white border border-slate-200 rounded-lg p-4'>
        <h2 className='text-sm font-semibold text-slate-800 mb-1'>Expedientes en gestión sin ninguna tarea ({datos.without_tasks.length})</h2>
        <p className='text-xs text-slate-500 mb-2'>Nadie los va a tocar si nadie los mira.</p>
        {datos.without_tasks.length > 0 && (
          <div className='mb-2'>
            <Buscador value={buscarSinTarea} onChange={setBuscarSinTarea} placeholder='Buscar deudor o estado' />
          </div>
        )}
        {datos.without_tasks.length === 0 ? (
          <p className='text-sm text-slate-500'>Todos tienen alguna tarea.</p>
        ) : sinTarea.length === 0 ? (
          <p className='text-sm text-slate-500'>Ninguno coincide con la búsqueda.</p>
        ) : (
          <ul className='divide-y divide-slate-100 text-sm'>
            {sinTarea.map(w => (
              <li key={w.expedient_id} className='flex items-center gap-3 py-2'>
                <Enlace id={w.expedient_id}>{w.deudor}</Enlace>
                <span className='text-slate-500'>{w.estado}</span>
                <span className='ml-auto text-slate-600'>
                  {w.importe_deuda} {w.divisa || 'EUR'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const AuditPage = () => {
  const { user } = userStore();
  const [vista, setVista] = useState('actividad');

  if (!['company', 'gestor'].includes(user?.role)) return <Navigate to='/dashboard' replace />;

  return (
    <MainLayout>
      <div className='space-y-4'>
        <header className='bg-white border border-slate-200 rounded-lg px-5 py-4'>
          <div className='flex items-center gap-2'>
            <ShieldCheckIcon className='h-6 w-6 text-blue-600' />
            <h1 className='text-2xl font-semibold text-slate-900'>Auditoría</h1>
          </div>
          <p className='text-sm text-slate-500 mt-1'>
            {user.role === 'gestor'
              ? 'Lo que habéis hecho tú y el agente de IA en tus expedientes, y lo que te queda por hacer.'
              : 'Todo lo que han hecho el agente de IA y tu equipo en vuestros expedientes AR, y lo que falta por hacer.'}
          </p>
        </header>
        <div className='inline-flex rounded-lg border border-slate-200 bg-white p-1' role='tablist'>
          {[
            { id: 'actividad', label: 'Actividad' },
            { id: 'pendiente', label: 'Pendiente' },
          ].map(t => (
            <button
              key={t.id}
              type='button'
              role='tab'
              aria-selected={vista === t.id}
              onClick={() => setVista(t.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                vista === t.id ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {vista === 'actividad' ? <Actividad /> : <Pendiente />}
      </div>
    </MainLayout>
  );
};

export default AuditPage;
